/**
 * The write mapping: this page's `RuleDraft` → the stored `rule_data` body that
 * `POST /rules` and `PUT /rules/:id` accept. The inverse of `rule-adapters.ts`.
 *
 * **1. Scope (plan §2.2).** With no breadcrumb, `ownership` means "what kind of thing
 * this rule targets", the ids live in `scope.values`, and BOTH id columns stay null
 * as the marker of a new-model rule:
 *
 * | For traffic from      | ownership  | ad_account_id | campaign_id | scope.type | scope.values |
 * | --------------------- | ---------- | ------------- | ----------- | ---------- | ------------ |
 * | {website} (All ads)   | WEBSITE    | null          | null        | WEBSITE    | []           |
 * | Selected ad accounts  | AD_ACCOUNT | null          | null        | ACCOUNT    | [aa…]        |
 * | Selected campaigns    | CAMPAIGN   | null          | null        | CAMPAIGNS  | [c…]         |
 *
 * The first row is what the old page writes for "This website"; the other two are
 * matched by the fourth bucket in `findCustomRules()` (§2.3). `ownership` is also
 * what `rule-trigger.ts` derives a block's blast radius from.
 *
 * **2. Values that fail silently.** An unmapped country list serialises to `$nin` of
 * NAMES, which matches every click (plan §4.2.1a), so every such conversion is routed
 * through `criteria.ts` or a table below.
 */
import {
	ClickStatus,
	CriteriaModifier,
	CriteriaModifierMode,
	CriteriaModifierType,
	CriteriaType,
	OwnershipType,
	RuleType,
	ScopeType,
	SourceType
} from 'api-models'
import { criterionDefinition, storedCriterionValue } from './criteria'
import { asnValues, ispValues, selectedAsnNames } from './network-values'
import { validTriggerThreshold } from './rule-options'
import { RuleEntities } from './rule-entities'
import { describeRule } from './rule-summary'
import { RuleCriterion, RuleDraft, RuleScope } from './types'

/** Drawer word → the enum the engine keys clicks by. */
const SOURCE_BY_FOCUS: Record<string, SourceType> = {
	Visitor: SourceType.VISITOR,
	'IP address': SourceType.IP,
	'IP range': SourceType.RANGE,
	Placement: SourceType.PLACEMENT,
	Device: SourceType.DEVICE
}

/** The drawer offers plurals, the engine stores the singular. Typed to the engine's
 *  own union, so a new drawer unit cannot reach a period field first. */
type StoredPeriodUnit = 'second' | 'minute' | 'hour' | 'day'

const UNIT_BY_DISPLAY: Record<string, StoredPeriodUnit> = {
	minutes: 'minute',
	hours: 'hour',
	days: 'day'
}

const STATUS_BY_DISPLAY: Record<string, ClickStatus> = {
	Valid: ClickStatus.GOOD,
	Suspicious: ClickStatus.SUSPICIOUS,
	Bad: ClickStatus.BAD
}

/** §2.2, as one table rather than three branches. */
const STORED_SCOPE: Record<
	RuleScope['mode'],
	{ ownership: OwnershipType; type: ScopeType }
> = {
	website: { ownership: OwnershipType.WEBSITE, type: ScopeType.WEBSITE },
	accounts: { ownership: OwnershipType.AD_ACCOUNT, type: ScopeType.ACCOUNT },
	campaigns: { ownership: OwnershipType.CAMPAIGN, type: ScopeType.CAMPAIGNS }
}

/** When each modifier is evaluated, and how long after the click. Lifted verbatim from
 *  `rule-form/utils.ts` `buildCriteriaModifier`. `mode` decides whether the engine can
 *  answer at click time; a POST modifier written as REALTIME never matches. */
const EVALUATION: Record<
	string,
	{ mode: CriteriaModifierMode; delay: number }
> = {
	[CriteriaModifierType.NO_DEVICE]: {
		mode: CriteriaModifierMode.POST,
		delay: 60
	},
	[CriteriaModifierType.BAD_CLICKS]: {
		mode: CriteriaModifierMode.REALTIME,
		delay: 0
	},
	[CriteriaModifierType.PROXY]: {
		mode: CriteriaModifierMode.REALTIME,
		delay: 0
	},
	[CriteriaModifierType.THREAT_LEVEL]: {
		mode: CriteriaModifierMode.REALTIME,
		delay: 0
	},
	[CriteriaModifierType.IP_USAGE]: {
		mode: CriteriaModifierMode.REALTIME,
		delay: 0
	},
	[CriteriaModifierType.DEVICE_TYPE]: {
		mode: CriteriaModifierMode.REALTIME,
		delay: 0
	},
	[CriteriaModifierType.ISP]: { mode: CriteriaModifierMode.REALTIME, delay: 0 },
	[CriteriaModifierType.ASN]: { mode: CriteriaModifierMode.REALTIME, delay: 0 },
	[CriteriaModifierType.COUNTRY]: {
		mode: CriteriaModifierMode.REALTIME,
		delay: 0
	},
	[CriteriaModifierType.INTERACTION]: {
		mode: CriteriaModifierMode.REALTIME,
		delay: 600
	},
	[CriteriaModifierType.BOT_PROBABILITY]: {
		mode: CriteriaModifierMode.POST,
		delay: 600
	},
	[CriteriaModifierType.TIME]: { mode: CriteriaModifierMode.POST, delay: 600 },
	[CriteriaModifierType.PAGE_VIEWS]: {
		mode: CriteriaModifierMode.POST,
		delay: 600
	},
	[CriteriaModifierType.SEARCH_KEYWORD]: {
		mode: CriteriaModifierMode.REALTIME,
		delay: 0
	},
	[CriteriaModifierType.CONVERSION]: {
		mode: CriteriaModifierMode.REALTIME,
		delay: 0
	}
}

/** A negated conversion fires when a conversion has NOT arrived, which is only
 *  knowable once the window has passed. */
const NOT_CONVERSION_EVALUATION = {
	mode: CriteriaModifierMode.POST,
	delay: 1200
}

export interface WriteContext {
	entities: RuleEntities
	/** Display name → ISO alpha-2, for the country criterion. */
	countryCodeByName: Map<string, string>
}

const isNegative = (criterion: RuleCriterion) => criterion.operator === 'is not'

/** `more than` / `less than` become an operand beside the value. */
const operandFor = (criterion: RuleCriterion) =>
	criterion.operator === 'less than' ? 'less' : 'more'

const numberValue = (criterion: RuleCriterion) =>
	Number(criterion.values[0] ?? 0) || 0

/** One selected row → one stored modifier, or `null` for a row this page cannot
 *  serialise: half-formed, the rule would fire and ignore a condition the author saw. */
const toModifier = (
	criterion: RuleCriterion,
	context: WriteContext
): CriteriaModifier | null => {
	const definition = criterionDefinition(criterion.id)
	if (!definition) return null

	const negative = isNegative(criterion)
	/** These DTO fields are literal unions and `criteria.ts` is the table that DEFINES
	 *  those tokens, so a value from `storedCriterionValue` is one by construction. The
	 *  compiler cannot follow that, so the assertion is made once here. */
	const stored = <T extends string>(): T[] =>
		criterion.values.map((value) =>
			storedCriterionValue(criterion.id, value)
		) as T[]
	const base = EVALUATION[definition.modifier]
	const modifier = { ...base, type: definition.modifier } as CriteriaModifier

	switch (definition.modifier) {
		case CriteriaModifierType.NO_DEVICE:
		case CriteriaModifierType.BAD_CLICKS:
		case CriteriaModifierType.PROXY:
			return modifier

		case CriteriaModifierType.THREAT_LEVEL:
			return { ...modifier, threat_level: { values: stored(), negative } }
		case CriteriaModifierType.IP_USAGE:
			return { ...modifier, ip_usage: { values: stored(), negative } }
		case CriteriaModifierType.DEVICE_TYPE:
			return { ...modifier, device_type: { values: stored(), negative } }
		case CriteriaModifierType.BOT_PROBABILITY:
			return { ...modifier, bot_probability: { values: stored(), negative } }
		case CriteriaModifierType.INTERACTION:
			return { ...modifier, user_interaction: { values: stored(), negative } }

		case CriteriaModifierType.ISP:
			return {
				...modifier,
				isp: { values: ispValues(criterion.values), negative }
			}
		case CriteriaModifierType.ASN: {
			const values = asnValues(criterion.values)
			return {
				...modifier,
				asn: {
					values,
					negative,
					names: selectedAsnNames(values, criterion.names)
				}
			}
		}

		case CriteriaModifierType.COUNTRY:
			// The one conversion that fails OPEN if skipped: `geo.country` holds ISO alpha-2,
			// and an unmapped `is not` becomes `$nin` of names. A name with no code is dropped,
			// so a rule can narrow less than intended but never widen to all traffic.
			return {
				...modifier,
				country: {
					values: criterion.values
						.map((name) => context.countryCodeByName.get(name))
						.filter((code): code is string => !!code),
					groups: null,
					negative
				}
			}

		case CriteriaModifierType.TIME:
			return {
				...modifier,
				time: {
					primary: { value: numberValue(criterion), unit: 'second' },
					operand: operandFor(criterion),
					negative: criterion.operator === 'less than'
				}
			}
		case CriteriaModifierType.PAGE_VIEWS:
			return {
				...modifier,
				page_views: {
					primary: numberValue(criterion),
					operand: operandFor(criterion),
					negative: criterion.operator === 'less than'
				}
			}

		case CriteriaModifierType.SEARCH_KEYWORD:
			return {
				...modifier,
				keyword_text: {
					values: [
						...new Set(
							criterion.values.map((value) => value.trim()).filter(Boolean)
						)
					],
					value: criterion.values[0]?.trim() ?? '',
					negative
				}
			}

		case CriteriaModifierType.CONVERSION:
			// `NOT_CONVERSION` exists only as the shape `mapFetchedRules` hands the drawer.
			return {
				...(negative ? NOT_CONVERSION_EVALUATION : base),
				type: CriteriaModifierType.CONVERSION,
				conversion: {
					negative,
					// Never on `is not`: the engine returns on `label` before it reads
					// `negative`, so the pair compiles to the POSITIVE condition.
					// `findProblems` refuses the draft; this keeps the shape unreachable.
					...(!negative && criterion.values[0]
						? { label: criterion.values[0] }
						: {})
				}
			} as CriteriaModifier

		default:
			return null
	}
}

/** The `rule_data` body for a create or an update. Actions mirror
 *  `prepareRuleParams`: blacklisting already contains both exclusions. */
export const prepareRuleData = (draft: RuleDraft, context: WriteContext) => {
	if (!validTriggerThreshold(draft.trigger.maxClicks))
		throw new Error('Choose a trigger from 1st to 20th click.')
	const { ownership, type } = STORED_SCOPE[draft.scope.mode]
	const percent = Number(draft.threshold.percent) || undefined

	return {
		name: draft.name.trim(),
		description: draft.description.trim(),
		ownership,
		// From the same helper the drawer previews with, not scraped out of the DOM.
		summary: describeRule(context.entities, draft),
		resolve_status:
			STATUS_BY_DISPLAY[draft.actions.clickStatus] ?? ClickStatus.BAD,
		focus: { source: SOURCE_BY_FOCUS[draft.trigger.focus], target: null },
		scope: {
			type,
			// Empty for the website scope: `scope-conditions.ts` returns early on WEBSITE.
			values: draft.scope.mode === 'website' ? [] : draft.scope.values,
			groups: null
		},
		actions: {
			block: draft.actions.blacklist
				? {
						period: {
							value: draft.actions.blacklistValue,
							unit: UNIT_BY_DISPLAY[draft.actions.blacklistUnit]
						}
				  }
				: null,
			// No control of its own, so a new one takes `buildCriteriaModifier`'s one-day
			// window while an existing one keeps whatever it was stored with.
			// Absent entirely when blacklisting, which already contains it.
			temp_ip_exclusion:
				draft.actions.blacklist || !draft.actions.tempIpExclusion
					? null
					: {
							period: draft.actions.tempIpExclusionPeriod ?? {
								value: 1,
								unit: 'day' as StoredPeriodUnit
							}
					  },
			// Sent as null rather than omitted: a missing key reads as "unchanged" to a merge.
			disrupt: null,
			audience_exclusion: draft.actions.blacklist
				? false
				: draft.actions.audienceExclusion,
			labels: draft.actions.addLabels
				? draft.actions.labels
						.split(',')
						.map((label) => label.trim())
						.filter(Boolean)
				: null
		},
		criteria: {
			type:
				draft.threshold.mode === 'all'
					? CriteriaType.SOURCE_FREQ
					: CriteriaType.SHARE,
			value: Number(draft.trigger.maxClicks) || 0,
			share_value: draft.threshold.mode === 'all' ? undefined : percent,
			period: {
				value: Number(draft.trigger.periodValue) || 0,
				unit: UNIT_BY_DISPLAY[draft.trigger.periodUnit]
			},
			modifiers: draft.criteria
				.map((criterion) => toModifier(criterion, context))
				.filter((modifier): modifier is CriteriaModifier => modifier !== null)
		},
		type: RuleType.CUSTOM
	}
}

/** The id columns a new-model rule carries: none. Both null is what tells the
 *  engine's fourth lookup bucket to read `scope.values`. */
export const NEW_MODEL_ID_COLUMNS = {
	ad_account_id: null,
	campaign_id: null
}
